import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const TIER_CONFIG = {
  "WITHIN NORMAL RANGE": { color: "#4ade80", bg: "bg-[#4ade80]/10", border: "border-[#4ade80]/30" },
  "EVEN": { color: "#4ade80", bg: "bg-[#4ade80]/10", border: "border-[#4ade80]/30" },
  "CLOSE": { color: "#4ade80", bg: "bg-[#4ade80]/10", border: "border-[#4ade80]/30" },
  "SLIGHT WIN": { color: "#45b7d1", bg: "bg-[#45b7d1]/10", border: "border-[#45b7d1]/30" },
  "CLEAR WIN": { color: "#fbbf24", bg: "bg-[#fbbf24]/10", border: "border-[#fbbf24]/30" },
  "LOPSIDED": { color: "#fbbf24", bg: "bg-[#fbbf24]/10", border: "border-[#fbbf24]/30" },
  "LOPSIDED+": { color: "#ff6b6b", bg: "bg-[#ff6b6b]/10", border: "border-[#ff6b6b]/30" },
  "VERY LOPSIDED": { color: "#ff3b30", bg: "bg-[#ff3b30]/10", border: "border-[#ff3b30]/30" },
  "EXTREME OUTLIER": { color: "#ff3b30", bg: "bg-[#ff3b30]/20", border: "border-[#ff3b30]/50" },
};

function PlayerSearch({ onSelect, placeholder }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const debounce = useRef(null);
  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const res = await axios.get(`${API}/players/search?q=${encodeURIComponent(q)}&numQbs=2&numTeams=12&ppr=1`);
      setResults((res.data || []).slice(0, 6)); setShow(true);
    } catch { setResults([]); }
  }, []);
  const handleChange = (e) => { setQuery(e.target.value); clearTimeout(debounce.current); debounce.current = setTimeout(() => search(e.target.value), 200); };
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#555]" />
        <input type="text" value={query} onChange={handleChange} onFocus={() => results.length > 0 && setShow(true)} placeholder={placeholder} className="w-full h-9 pl-8 pr-3 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none" autoComplete="off" />
      </div>
      {show && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#171717] border border-[#262626] rounded-sm z-50 shadow-lg">
          {results.map((p, i) => (
            <button key={i} onClick={() => { onSelect({ name: p.name, type: "player" }); setQuery(""); setShow(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#262626] text-xs border-b border-[#1a1a1a] last:border-0">
              <span className="text-white">{p.name}</span><span className="text-[#555] ml-auto">{p.position} · {p.team}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PickSelector({ onSelect }) {
  const [round, setRound] = useState(1);
  const [season, setSeason] = useState("2026");
  return (
    <div className="flex items-center gap-2 mt-2">
      <select value={season} onChange={e => setSeason(e.target.value)} className="h-8 px-2 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm">
        {["2025","2026","2027","2028"].map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select value={round} onChange={e => setRound(parseInt(e.target.value))} className="h-8 px-2 bg-[#0a0a0a] border border-[#262626] text-white text-xs rounded-sm">
        {[1,2,3,4,5].map(r => <option key={r} value={r}>Round {r}</option>)}
      </select>
      <button onClick={() => onSelect({ name: `${season} Round ${round} Pick`, type: "pick", round, season })} className="h-8 px-3 text-[10px] font-bold text-[#ccff00] border border-[#ccff00]/30 rounded-sm hover:bg-[#ccff00]/10">+ PICK</button>
    </div>
  );
}

export default function TradeFairnessAnalyzer() {
  const [sideA, setSideA] = useState([]);
  const [sideB, setSideB] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    if (!sideA.length || !sideB.length) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/trades/check-fairness`, { side_a: sideA, side_b: sideB });
      setResult(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const tc = result ? (TIER_CONFIG[result.verdict] || TIER_CONFIG["LOPSIDED"]) : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Trade Fairness Analyzer: Is This Dynasty Trade Fair? Data From 125,000+ Real Trades — Dynasty Trade Maker" description="Input any dynasty trade and see exactly how lopsided it is, measured against 125,000+ real Sleeper trades. Built for commissioners and managers who want data, not guesses." path="/tools/trade-fairness" />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">MEASURED AGAINST 125,000+ REAL TRADES</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">TRADE FAIRNESS <span className="text-[#ccff00]">ANALYZER</span></h1>
        <p className="text-sm text-[#a3a3a3] mb-6">Input any trade and see exactly how lopsided it is, measured against 125,000+ real dynasty trades. Built for commissioners and managers who want data, not guesses.</p>

        {/* Trade Builder */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {[{ label: "SIDE A", items: sideA, setItems: setSideA }, { label: "SIDE B", items: sideB, setItems: setSideB }].map(side => (
            <div key={side.label} className="border border-[#262626] bg-[#171717] p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">{side.label}</p>
              <PlayerSearch onSelect={item => side.setItems([...side.items, item])} placeholder="Search player..." />
              <PickSelector onSelect={item => side.setItems([...side.items, item])} />
              <div className="mt-3 space-y-1">
                {side.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-[#0a0a0a] border border-[#262626] rounded-sm">
                    <span className="text-xs text-white">{item.name}</span>
                    <button onClick={() => side.setItems(side.items.filter((_, j) => j !== i))} className="text-[#555] hover:text-[#ff3b30]"><X size={12} /></button>
                  </div>
                ))}
                {side.items.length === 0 && <p className="text-[10px] text-[#555] text-center py-2">Add players or picks</p>}
              </div>
            </div>
          ))}
        </div>

        <button onClick={analyze} disabled={!sideA.length || !sideB.length || loading} className="w-full h-11 btn-primary rounded-sm text-sm font-bold uppercase mb-6 disabled:opacity-40" data-testid="analyze-btn">
          {loading ? "ANALYZING..." : "ANALYZE TRADE"}
        </button>

        {/* Result */}
        {result && (
          <div className="border border-[#262626] bg-[#171717] p-6 mb-6" data-testid="fairness-result">
            {/* Headline */}
            <div className="text-center mb-6">
              <p className="text-sm text-[#a3a3a3] mb-2">This trade has a</p>
              <p className="font-mono text-5xl font-bold text-white">{result.gap_pct}%</p>
              <p className="text-sm text-[#a3a3a3] mt-1">value gap</p>
              <p className="text-xs text-[#888] mt-2">That's more lopsided than <strong className="text-white">{result.percentile}%</strong> of the 125,000+ dynasty trades we've analyzed.</p>
            </div>

            {/* Distribution visualization */}
            <div className="mb-6 px-2">
              <div className="relative h-8 bg-[#0a0a0a] rounded-full overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#4ade80] via-[#fbbf24] via-[#ff6b6b] to-[#ff3b30] opacity-20" />
                {/* Marker labels */}
                <div className="absolute top-0 left-[50%] h-full w-px bg-[#333]" />
                <div className="absolute top-0 left-[75%] h-full w-px bg-[#333]" />
                <div className="absolute top-0 left-[90%] h-full w-px bg-[#333]" />
                {/* This trade marker */}
                <div className="absolute top-0 bottom-0 w-1.5 bg-white rounded-full shadow-lg shadow-white/30" style={{ left: `${Math.min(result.percentile, 99.5)}%` }} />
              </div>
              <div className="flex justify-between text-[8px] text-[#555] mt-1 px-1">
                <span>Even</span><span>Median (39%)</span><span>Top 25%</span><span>Top 10%</span><span>Top 1%</span>
              </div>
            </div>

            {/* Tier badge */}
            <div className={`flex items-center justify-center gap-3 px-4 py-3 border rounded-sm mb-6 ${tc?.bg} ${tc?.border}`}>
              <span className="font-heading text-lg font-bold uppercase" style={{ color: tc?.color }}>{result.verdict}</span>
              <span className="text-xs text-[#888]">
                {result.gap_pct < 75 ? "Dynasty trades are naturally uneven (avg gap is 40%). This is normal." :
                 result.gap_pct < 85 ? "More one-sided than ~90% of dynasty trades." :
                 result.gap_pct < 96 ? "Statistically unusual — top ~5% of all trades." :
                 "Top 1% of all dynasty trades. Rare. Worth a closer look."}
              </span>
            </div>

            {/* Values */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[{ label: "SIDE A", val: result.value_a, items: result.side_a, win: result.winner === "a" },
                { label: "SIDE B", val: result.value_b, items: result.side_b, win: result.winner === "b" }].map(s => (
                <div key={s.label} className={`p-3 border rounded-sm ${s.win ? "border-[#ccff00]/30 bg-[#ccff00]/5" : "border-[#262626]"}`}>
                  <div className="flex justify-between mb-2">
                    <span className="text-[9px] font-bold uppercase text-[#555]">{s.label}</span>
                    <span className="text-xs font-mono font-bold text-white">{s.val.toLocaleString()}</span>
                  </div>
                  {s.items.map((p, i) => <p key={i} className="text-[10px] text-[#888]">{p.name}: {p.value.toLocaleString()}</p>)}
                </div>
              ))}
            </div>

            {/* Explanation */}
            <div className="px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-sm mb-4">
              <p className="text-sm text-[#a3a3a3] leading-relaxed">{result.explanation}</p>
            </div>

            {/* Interpretive section — only for extreme trades */}
            {result.gap_pct >= 75 && (
              <div className="px-4 py-4 border border-[#262626] rounded-sm">
                <p className="text-xs font-bold text-white mb-2">What a large gap does and doesn't tell you</p>
                <p className="text-[11px] text-[#888] leading-relaxed mb-3">A gap this size means the value is heavily one-sided. It does not, on its own, mean anything improper happened. In our data, the most lopsided trades are usually explained by:</p>
                <ul className="text-[11px] text-[#888] space-y-1 mb-3 pl-4 list-disc">
                  <li>A rebuilding team selling win-now players for future picks (often intentionally uneven)</li>
                  <li>A manager who simply misjudged a player's value</li>
                  <li>An injury or news event that changed a player's worth right around the trade</li>
                  <li>Two managers who know it's uneven and don't care</li>
                </ul>
                <p className="text-xs font-bold text-white mb-2">If you're a commissioner reviewing this trade:</p>
                <ul className="text-[11px] text-[#888] space-y-1 pl-4 list-disc">
                  <li>Is one side clearly rebuilding? A veteran-for-picks dump can look extreme and be completely legitimate.</li>
                  <li>Did a player's situation change (injury, benching, trade) right before the deal?</li>
                  <li>Do the two managers have a pattern of one-sided trades with each other specifically?</li>
                  <li>Does the "winning" side already have a commanding lead they're extending?</li>
                </ul>
                <p className="text-[10px] text-[#555] mt-3 italic">The data flags the trade for your attention. The judgment is yours.</p>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mt-4 text-center">
              <div><p className="text-[9px] text-[#555] uppercase">Z-Score</p><p className="text-xs font-mono text-white">{result.z_score}σ</p></div>
              <div><p className="text-[9px] text-[#555] uppercase">Dataset</p><p className="text-xs font-mono text-white">{result.dataset?.total_trades?.toLocaleString()}</p></div>
              <div><p className="text-[9px] text-[#555] uppercase">Avg Gap</p><p className="text-xs font-mono text-white">{result.dataset?.mean_gap}%</p></div>
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <Link to="/trade-calculator" className="flex items-center justify-between p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
            <div><p className="text-sm text-white font-semibold">Trade Calculator</p><p className="text-[10px] text-[#666]">Quick value comparison</p></div>
            <ArrowRight size={14} className="text-[#555]" />
          </Link>
          <Link to="/research/how-lopsided-is-the-average-trade" className="flex items-center justify-between p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
            <div><p className="text-sm text-white font-semibold">The 40% Finding</p><p className="text-[10px] text-[#666]">Why dynasty trades are naturally lopsided</p></div>
            <ArrowRight size={14} className="text-[#555]" />
          </Link>
        </div>

        <p className="text-[9px] text-[#555] text-center italic">This tool measures value differential against real dynasty trade data. It is an analytical aid, not a judgment about any person's conduct. Extreme value gaps have many innocent explanations.</p>
      </div>
    </div>
  );
}
